import { computed, onBeforeMount } from 'vue'

import { ParagraphType } from '@xcel-next/core/enums'

import { useConfigStore } from '../stores/config'

import type { Ref } from 'vue'
import type { Brand } from '@xcel-next/core/enums'
import type { ComposedBody, ComposedMetaData, ComposedPage, ComposedPageMeta } from '@xcel-next/composer-schema'

declare global {
  interface Window {
    fuckAdBlock?: {
      onDetected: (callback: () => void) => void,
      onNotDetected: (callback: () => void) => void
    }
  }
}

type CustomDimensions = {
  brand: Brand,
  categories: string,
  mainCategory: string | undefined,
  subCategory: string | undefined,
  mainTag: string | undefined,
  pageType: string,
  articleType: string | undefined,
  authorFrontend: string | undefined,
  authorBackend: ComposedMetaData['creator'],
  tags: string[],
  title: ComposedBody['headline'],
  roofline: ComposedBody['roofline'],
  wordCount: ComposedMetaData['wordCount'],
  charCount: ComposedMetaData['charCount'],
  hasVideo: ComposedMetaData['hasVideo'],
  hasGallery: ComposedMetaData['hasGallery'],
  isDuplicate: ComposedMetaData['isDuplicate'],
  amazonCoop: boolean,
  dateCreated: ComposedMetaData['dateCreated'],
  dateChanged: ComposedMetaData['dateChanged'],
  displayDate: ComposedBody['displayDate'],
  id: ComposedPageMeta['id'],
  nodeId: ComposedPageMeta['nodeId'],
  campaignName: ComposedMetaData['campaignName'],
  evergreenContent: boolean,
  isArticle: boolean,
  isNewsArticle: boolean,
  isRecipe: boolean,
  isCollectionPage: boolean,
  affiliateContent: boolean,
  isVideoOnlyArticle: boolean,
  hideGooglePreferredSourcesButton: boolean,
  shorttermContent: boolean,
  isContentHubContent: boolean,
  aiGeneratedContent: boolean,
  isContentCommerce: boolean,
  coreContentPurpose: ComposedMetaData['contentCommerceFormat'],
  newsContent: boolean,
  cocoContent: boolean,
  paragraphTypes: string,
  embedTypes: string,
  videoIds: string,
  indAdKeyWord?: string,
  avtAdKeyWord?: string
}

export const useCustomDimensions = (brand: Brand, pageData: Ref<ComposedPage>) => {
  const urlRegexes = {
    epgChannel: /\/tv\/sender-/,
    epgBroadcast: /\/tv\/[\S].*-epg-[\d]*/,
    epgOverview: /\/tv\//,
    vod: /\/wer-streamt\//,
    sweepstakes: /\/gewinnspiele/
  }

  const pageMeta = computed(() => pageData.value.pageMeta)
  const metaData = computed(() => pageData.value.metaData)
  const configStore = useConfigStore()

  const googleTagManagerId = computed(() => configStore.rsConfig.googleTagManagerId)

  const getPageType = (mainTags: string[], tags: string[]): string => {
    if (metaData.value.adAAdZone === 'homepage') {
      return 'homepage'
    }

    if (pageMeta.value.structureTermType === 'static') {
      return 'static'
    }

    if (pageMeta.value.structureTermType === 'author') {
      return 'author'
    }

    const gameTags = ['Games', 'Gaming']
    if (gameTags.some(tag => mainTags.includes(tag) || tags.includes(tag) || /\/gaming$/.test(pageMeta.value.url ?? ''))) {
      return 'games'
    }

    if (urlRegexes.sweepstakes.test(pageMeta.value.url ?? '')) {
      return 'sweepstakes'
    }

    if (urlRegexes.epgChannel.test(pageMeta.value.url ?? '')) {
      return 'epgChannel'
    }

    if (urlRegexes.epgBroadcast.test(pageMeta.value.url ?? '')) {
      return 'epgBroadcast'
    }

    if (urlRegexes.epgOverview.test(pageMeta.value.url ?? '')) {
      return 'epgOverview'
    }

    if (urlRegexes.vod.test(pageMeta.value.url ?? '')) {
      return 'vod'
    }

    if (pageMeta.value.isSeriesMaster || pageMeta.value.isSeriesMember) {
      return 'seriesArticle'
    }

    if (pageMeta.value.pageType === 'article' || pageMeta.value.nodeId) {
      return 'article'
    }

    return 'overview'
  }

  const getArticleType = () => {
    const { pageMeta: { url, articleType } } = pageData.value

    if (urlRegexes.epgChannel.test(url ?? '')) {
      return 'epgChannel'
    }

    if (urlRegexes.epgBroadcast.test(url ?? '')) {
      return 'epgBroadcast'
    }

    if (urlRegexes.vod.test(url ?? '')) {
      return 'vod'
    }

    return articleType
  }

  const generateCustomDimensionsFromPageData = () => {
    if (pageData.value?.body) {
      const mainTags = pageData.value.body.mainStructurePath
        ? pageData.value.body.mainStructurePath
          .filter(tag => tag.url && tag.url !== '/')
          .map(tag => tag.name)
          .filter((name): name is string => name !== undefined)
        : []
      const tags = Array.from(new Set([
        ...mainTags,
        ...(pageData.value.body.additionalStructures
          ? pageData.value.body.additionalStructures
            .map(tag => tag.name)
            .filter((name): name is string => name !== undefined)
          : [])
      ]))
      const videoIds: string[] = []
      const paragraphTypes: string[] = []
      const embedTypes: string[] = []
      if (pageData.value.body.paragraphs) {
        pageData.value.body.paragraphs.forEach(paragraph => {
          if (paragraph.paragraphType === ParagraphType.embed) {
            const embedType = paragraph.embed?.embedType
            if (embedType && !embedTypes.includes(embedType)) embedTypes.push(embedType)
            paragraphTypes.push(`${paragraph.paragraphType}(${embedType})`)
          } else {
            const jwId = paragraph.video?.jwId
            if (paragraph.paragraphType === ParagraphType.video && jwId) videoIds.push(jwId)
            paragraphTypes.push(paragraph.paragraphType)
          }
        })
      }
      const customDimensions: CustomDimensions = {
        brand,
        categories: mainTags.join('|'),
        mainCategory: mainTags[0],
        subCategory: mainTags[1],
        mainTag: mainTags.slice(-1)[0],
        pageType: getPageType(mainTags, tags),
        articleType: getArticleType(),
        authorFrontend: pageData.value.body.author ? pageData.value.body.author.titleLong : '',
        authorBackend: metaData.value.creator,
        tags,
        title: pageData.value.body.headline,
        roofline: pageData.value.body.roofline,
        wordCount: metaData.value.wordCount,
        charCount: metaData.value.charCount,
        hasVideo: metaData.value.hasVideo,
        hasGallery: metaData.value.hasGallery,
        isDuplicate: metaData.value.isDuplicate,
        amazonCoop: metaData.value.cooperations ? metaData.value.cooperations.indexOf('Amazon') !== -1 : false,
        dateCreated: metaData.value.dateCreated,
        dateChanged: metaData.value.dateChanged,
        displayDate: pageData.value.body.displayDate,
        id: pageData.value.pageMeta.id,
        nodeId: pageData.value.pageMeta.nodeId,
        campaignName: metaData.value.campaignName,
        evergreenContent: !!metaData.value.evergreenContent,
        isArticle: !!metaData.value.structuredDataOverrides?.isArticle,
        isNewsArticle: !!metaData.value.structuredDataOverrides?.isNewsArticle,
        isRecipe: !!metaData.value.structuredDataOverrides?.isRecipe,
        isCollectionPage: !!metaData.value.structuredDataOverrides?.isCollectionPage,
        affiliateContent: !!metaData.value.affiliateContent,
        isVideoOnlyArticle: !!metaData.value.isVideoOnlyArticle,
        hideGooglePreferredSourcesButton: !!metaData.value.hideGooglePreferredSourcesButton,
        shorttermContent: !!metaData.value.shorttermContent,
        isContentHubContent: !!metaData.value.isContentHubContent,
        aiGeneratedContent: !!metaData.value.aiGeneratedContent,
        isContentCommerce: !!metaData.value.isContentCommerce,
        coreContentPurpose: metaData.value.contentCommerceFormat,
        newsContent: !!metaData.value.newsContent,
        cocoContent: !!metaData.value.cocoContent,
        paragraphTypes: paragraphTypes.join(' | '),
        embedTypes: embedTypes.join(' | '),
        videoIds: videoIds.join(' | ')
      }
      if (metaData.value.adKeywords && metaData.value.adKeywords.length > 0) {
        const adKeyWord = metaData.value.adKeywords[0]
        if (adKeyWord.startsWith('ind_')) {
          customDimensions.indAdKeyWord = adKeyWord.slice(4)
        } else {
          customDimensions.avtAdKeyWord = adKeyWord.startsWith('avt_') ? adKeyWord.slice(4) : adKeyWord
        }
      }
      return customDimensions
    }
  }

  onBeforeMount(() => {
    if (window && googleTagManagerId.value && process.env.NODE_ENV/*  !== 'development' */) {
      window.dataLayer = window.dataLayer || []
      if (typeof window.fuckAdBlock !== 'undefined') {
        window.fuckAdBlock.onDetected(() => {
          window.dataLayer.push({
            event: 'fuckadblock',
            adBlockerDetected: true
          })
        })
        window.fuckAdBlock.onNotDetected(() => {
          window.dataLayer.push({
            event: 'fuckadblock',
            adBlockerDetected: false
          })
        })
      }
      const queryParamReferrer = window.location.search.match(/referrer=(.+)/)
      queryParamReferrer && queryParamReferrer[1] && window.dataLayer.push({
        documentReferrer: decodeURIComponent(queryParamReferrer[1])
      })
      const data = generateCustomDimensionsFromPageData()
      if (!data) return
      window.dataLayer.push({ ...data, tags: data.tags.join('|') })
    }
  })

  return {
    generateCustomDimensionsFromPageData,
    getArticleType,
    getPageType
  }
}
